import { useEffect, useRef, useState, useCallback } from "react";
import io from "socket.io-client";
import Singleton from "../../utility/socketControl";
import { usePostData, useGetData } from "../../hooks/serviceGetways";
import { useHistory } from "react-router-dom";

const server = ["https://socket.haremaltin.com", "https://hrmsocketonly.haremaltin.com"];
const DATA_TIMEOUT_MS = 60_000; // 1 dk
 

const useLiveDataFetcher = (currencies) => {
    const socketNumRef = useRef(0);
    const [isLeader, setIsLeader] = useState(false);
    const [clientId, setClientId] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const postData = usePostData();
    const getData = useGetData();
    const history = useHistory();
    const historyRef = useRef(history);
    const externalSocketRef = useRef(null);
    const heartbeatIntervalRef = useRef(null);
    const mainSocketRef = useRef(null);
    const currenciesRef = useRef(currencies);
    const isLeaderRef = useRef(false);
    const dataTimeoutRef = useRef(null); // ⏱️ 1 dakikalık data timeout
    const lastDataTimeRef = useRef(null); // 📊 Son veri gelme zamanı
    
    useEffect(() => {
        historyRef.current = history;
    }, [history]);
    
    useEffect(() => {
        currenciesRef.current = currencies;
    }, [currencies]);

    useEffect(() => {
        isLeaderRef.current = isLeader;
    }, [isLeader]);

    // ⏱️ 1 Dakika Data Timeout Fonksiyonları
    const armDataTimeout = useCallback(() => {
        if (dataTimeoutRef.current) {
            clearTimeout(dataTimeoutRef.current);
        }

        dataTimeoutRef.current = setTimeout(() => {
            console.log('[LiveData] 🚨 NO DATA RECEIVED FOR 1 MINUTE');
            console.log('[LiveData] Last data time:', new Date(lastDataTimeRef.current).toISOString());

            // 🔁 Leader ise önce liderliği sunucuya bırak ki standby hemen devralsın,
            // reload'un beforeunload temizliğine güvenmeyelim (her tarayıcıda garanti değil).
            if (isLeaderRef.current && mainSocketRef.current) {
                console.log('[LiveData] Releasing leadership before reload so standby can take over immediately');
                mainSocketRef.current.emit('leader:release');
            }

            window.location.reload();
        }, DATA_TIMEOUT_MS);
    }, []);

    const startDataTimeout = useCallback(() => {
        console.log('[LiveData] ⏱️ Starting 1-minute data timeout...');
        lastDataTimeRef.current = Date.now();
        armDataTimeout();
    }, [armDataTimeout]);

    const resetDataTimeout = useCallback(() => {
        const now = Date.now();
        const timeSinceLastData = lastDataTimeRef.current ? (now - lastDataTimeRef.current) / 1000 : 0;

        if (timeSinceLastData > 10) { // Sadece 10 saniyeden fazla geçmişse log bas
            console.log(`[LiveData] 📊 Data received! Resetting 1-minute timeout. (Last data: ${timeSinceLastData.toFixed(0)}s ago)`);
        }

        lastDataTimeRef.current = now;
        armDataTimeout();
    }, [armDataTimeout]);

    const clearDataTimeout = useCallback(() => {
        console.log('[LiveData] Clearing data timeout...');
        if (dataTimeoutRef.current) {
            clearTimeout(dataTimeoutRef.current);
            dataTimeoutRef.current = null;
        }
        lastDataTimeRef.current = null;
    }, []);

    const connectToExternalSocket = useCallback(() => {
        console.log('[LiveData] connectToExternalSocket called');
        if (externalSocketRef.current) {
            console.log('[LiveData] External socket already exists, skipping');
            return;
        }

        const socketNum = socketNumRef.current % 2;
        console.log('[LiveData] Creating external socket connection to:', server[socketNum]);

        const socket = io.connect(server[socketNum], {
            transports: ["websocket"],
            extraHeaders: {
                'Origin': server[socketNum] === "https://socket.haremaltin.com"
                    ? 'https://canlipiyasalar.haremaltin.com'
                    : 'https://hrmsocketonly.haremaltin.com',
                'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/143.0.0.0 Safari/537.36',
                'Accept-Language': 'tr,en;q=0.9',
                'Accept-Encoding': 'gzip, deflate, br, zstd',
                'Cache-Control': 'no-cache',
                HOST: server[socketNum] === "https://socket.haremaltin.com"
                    ? 'socket.haremaltin.com'
                    : 'hrmsocketonly.haremaltin.com',
                'Pragma': 'no-cache',
                'Sec-WebSocket-Version': '13',
                'Sec-WebSocket-Key': 'Ax2c26nyBc0pKnaPOTjWEg==',
                'Sec-WebSocket-Extensions': 'permessage-deflate; client_max_window_bits',
                'Upgrade': 'websocket'
            },
            forceNew: true,
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 2000,
            timeout: 15000,
            upgrade: true,
            rememberUpgrade: false
        });

        externalSocketRef.current = socket;

        socket.on('connect', () => {
            console.log('[LiveData] External WebSocket CONNECTED as leader to', server[socketNum]);
            // ⏱️ Bağlantı kurulduğunda 1 dakikalık data timeout başlat
            startDataTimeout();
        });

        socket.on('disconnect', (reason) => {
            console.log('[LiveData] External WebSocket DISCONNECTED:', reason);
            // Bağlantı kesildiğinde timeout'u temizle
            clearDataTimeout();
        });

        socket.on('connect_error', (error) => {
            console.log('[LiveData] External Connection ERROR:', error.message || error);
            socketNumRef.current++;
        });

        socket.on("price_changed", function (rec_data) {
            // ⏱️ Veri geldi - timeout'u sıfırla
            resetDataTimeout();
            
            if (isLeaderRef.current && currenciesRef.current?.length > 0) {
                const payload = {
                    rec_data,
                    currencies: currenciesRef.current,
                    internal: true
                };

                postData("processData/create", payload)
                    .then(res => {
                        console.log('[LiveData] ✅ Data sent successfully:', res?.result);
                    })
                    .catch(err => {
                        console.log('[LiveData] ❌ Error sending data:', err);
                        
                        // 🔐 Authentication hatası kontrolü
                        if (err?.code === 401 || err?.message?.includes('token') || err?.message?.includes('Unauthorized')) {
                            console.log('[LiveData] 🚨 Authentication error during data send - redirecting to /asd');
                            if (historyRef.current) {
                                historyRef.current.push('/asd');
                            }
                            
                            // External socket'i disconnect et
                            if (externalSocketRef.current) {
                                externalSocketRef.current.disconnect();
                                externalSocketRef.current = null;
                            }
                        }
                    });
            } else {
                console.log('[LiveData] Skipping - not leader or no currencies');
            }
        });
    }, [postData, startDataTimeout, resetDataTimeout, clearDataTimeout]);

    const disconnectExternalSocket = useCallback(() => {
        console.log('[LiveData] Stopping data fetch (no longer leader)');

        // ⏱️ Data timeout'u temizle
        clearDataTimeout();

        if (externalSocketRef.current) {
            externalSocketRef.current.disconnect();
            externalSocketRef.current = null;
        }
    }, [clearDataTimeout]);

    const startHeartbeat = useCallback((interval) => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
        }

        heartbeatIntervalRef.current = setInterval(() => {
            if (mainSocketRef.current) {
                mainSocketRef.current.emit('leader:heartbeat', {}, (response) => {
                    if (!response?.isLeader) {
                        console.log('[LiveData] Lost leadership via heartbeat');
                        setIsLeader(false);
                        disconnectExternalSocket();
                        clearInterval(heartbeatIntervalRef.current);
                        heartbeatIntervalRef.current = null;
                    }
                });
            }
        }, interval || 5000);
    }, [disconnectExternalSocket]);

    const stopHeartbeat = useCallback(() => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }
    }, []);

    // 🔐 GÜVENLİK: Sadece giriş yapmış kullanıcılar veri gönderebilir
    useEffect(() => {
        console.log('[LiveData] Checking user authentication via /api/me...');
        setIsCheckingAuth(true);

        getData("me", {})
            .then((response) => {
                if (response?.result) {
                    console.log('[LiveData] ✅ User authenticated:', response.result);
                    setIsAuthenticated(true);
                } else {
                    console.log('[LiveData] ❌ User not authenticated');
                    setIsAuthenticated(false);
                }
            })
            .catch((error) => {
                console.log('[LiveData] ❌ Authentication check failed:', error);
                setIsAuthenticated(false);
            })
            .finally(() => {
                setIsCheckingAuth(false);
            });
    }, [getData]);

    useEffect(() => {
        // Sadece authenticated kullanıcılar leader election'a katılabilir
        if (isCheckingAuth) {
            console.log('[LiveData] Waiting for authentication check...');
            return;
        }

        if (!isAuthenticated) {
            console.log('[LiveData] 🚫 User not authenticated - skipping leader election');
            console.log('[LiveData] 💡 Only logged-in users can send data to the server');
            return;
        }

        console.log('[LiveData] Main useEffect starting...');
        const mainSocket = Singleton.getInstance();
        console.log('[LiveData] Got main socket:', mainSocket?.id, 'connected:', mainSocket?.connected);
        mainSocketRef.current = mainSocket;

        let storedClientId = localStorage.getItem('dataFetcherClientId');
        if (!storedClientId) {
            storedClientId = 'client_' + Math.random().toString(36).substring(2, 11);
            localStorage.setItem('dataFetcherClientId', storedClientId);
        }
        setClientId(storedClientId);

        const joinLeaderElection = () => {
            console.log('[LiveData] Emitting leader:join with clientId:', storedClientId);
            mainSocket.emit('leader:join', { clientId: storedClientId }, (response) => {
                console.log('[LiveData] leader:join response:', response);

                if (response?.isLeader) {
                    console.log('[LiveData] I am LEADER! Connecting to external socket...');
                    setIsLeader(true);
                    connectToExternalSocket();
                    startHeartbeat(response.heartbeatInterval);
                } else {
                    console.log('[LiveData] I am STANDBY. Current leader:', response?.currentLeader);
                }
            });
        };

        const handleElected = (data) => {
            setIsLeader(true);
            connectToExternalSocket();
            startHeartbeat(5000);
        };

        const handleStatus = (data) => {
            if (!data.isLeader) {
                setIsLeader(false);
                disconnectExternalSocket();
                stopHeartbeat();
            }
        };

        mainSocket.on('leader:elected', handleElected);
        mainSocket.on('leader:status', handleStatus);

        // Socket bağlıysa hemen join et, değilse connect event'ini bekle
        if (mainSocket.connected) {
            console.log('[LiveData] Main socket already connected, joining immediately');
            joinLeaderElection();
        } else {
            console.log('[LiveData] Main socket not connected, waiting for connect event...');
            mainSocket.on('connect', () => {
                console.log('[LiveData] Main socket connected! Now joining leader election');
                joinLeaderElection();
            });
        }

        return () => {
            stopHeartbeat();
            disconnectExternalSocket();
            mainSocket.emit('leader:release');
            mainSocket.off('leader:elected', handleElected);
            mainSocket.off('leader:status', handleStatus);
            mainSocket.off('connect', joinLeaderElection);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isCheckingAuth]);

    return { isLeader, clientId, isAuthenticated, isCheckingAuth };
};

export default useLiveDataFetcher;
